import { memo, useEffect } from 'react'
import { useI18n } from '@muse/i18n'
import { isUntrackedPage, loadGtm, sendEvent, setCookiesPolicy } from '@core/analytics'
import { useIsHydrating, useLocation } from '@muse-web/router'
import { useActions, useValues } from '@core/store'

import { CookieBanner, type TCookieBannerLabels } from '../components/cookie-banner'
import { COOKIE_POLICY_HREF } from '../constants/cookiePolicy'
import { isConsentExemptPath } from '../helpers/isConsentExemptPath'
import { cookieConsentStore } from '../store/cookieConsentStore'

export const CookieConsentContainer = memo(() => {
    const { t } = useI18n()
    const isHydrating = useIsHydrating()
    const { pathname } = useLocation()
    const [consent, gtmId, isEuVisitor] = useValues([
        cookieConsentStore.props.getCookieConsent,
        cookieConsentStore.props.getGtmId,
        cookieConsentStore.props.getIsEuVisitor,
    ])
    const actions = useActions({ setConsent: cookieConsentStore.actions.setCookieConsent })

    const gtmEnabled = gtmId !== ''
    const isExempt = isConsentExemptPath(pathname)
    const isUntracked = isUntrackedPage(pathname)
    const shouldShowBanner =
        !isHydrating &&
        gtmEnabled &&
        isEuVisitor &&
        (consent === '' || consent === 'informed') &&
        !isExempt &&
        !isUntracked

    useEffect(() => {
        if (isUntracked) {
            return
        }

        if (gtmEnabled && (!isEuVisitor || isExempt || consent !== 'declined')) {
            loadGtm(gtmId)
        }
    }, [gtmEnabled, isEuVisitor, isExempt, isUntracked, consent, gtmId])

    // First time the banner is shown to a never-seen visitor: record it and report the impression.
    useEffect(() => {
        if (shouldShowBanner && consent === '') {
            actions.setConsent('informed')
            setCookiesPolicy('informed')
            sendEvent({ name: 'cookie_modal_shown', payload: { placement: 'cookie_modal' } })
        }
    }, [shouldShowBanner, consent, actions])

    if (!shouldShowBanner) {
        return null
    }

    const labels: TCookieBannerLabels = {
        title: t('i18n.cookieConsentTitle'),
        description: t('i18n.cookieConsentDescription'),
        learnMore: t('i18n.cookieConsentLearnMore'),
        aboutPolicy: t('i18n.cookieConsentAboutPolicy'),
        decline: t('i18n.cookieConsentDecline'),
        allow: t('i18n.cookieConsentAllow'),
    }

    const handleAccept = () => {
        actions.setConsent('allowed')
        setCookiesPolicy('allowed')
        sendEvent({ name: 'cookie_modal_accept', payload: { placement: 'cookie_modal' } })
    }

    const handleDecline = () => {
        actions.setConsent('declined')
        setCookiesPolicy('declined')
        sendEvent({ name: 'cookie_modal_decline', payload: { placement: 'cookie_modal' } })
    }

    return (
        <CookieBanner
            labels={labels}
            learnMoreHref={COOKIE_POLICY_HREF}
            onAccept={handleAccept}
            onDecline={handleDecline}
        />
    )
})
